import React, { useCallback, useMemo } from 'react';
import {
  useGetSingleProductQuery,
  useGetSingleStoreQuery,
  useGetSkuQuery,
} from '../../../apiDetail';
import { ColumnsType } from 'antd/es/table';
import {
  DataTable,
  EntityId,
  IconButton_IconSizes,
  Label,
  ReadOnlyItem,
  Row,
  Text,
} from '@ydistri/ds';
import { useDetail, useDetailSkuEntity } from '../../../hooks/useDetail';
import SimpleList from './SimpleList';
import { useGetCategoryTreeQuery } from '../../../../../components/global/CategoryTree/apiCategoryTree';
import { styled } from 'styled-components';
import useCategoryId from '../../../../../hooks/useCategoryId';
import {
  constructCategoryPath,
  getCategories,
  isRootCategory,
} from '../../../../../lib/categories/categoriesLib';
import CategoriesList from './CategoriesList';
import { createSearchParams, generatePath, useSearchParams } from 'react-router';
import { ROUTES } from '../../../../../components/menu/getMenuLeftItems';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../store';
import { UrlParams } from '../../../detailTypes';
import YLink from '../../../../../components/global/Link/YLink';
import { useGetRegionsQuery } from '../../../../../apis/apiLists';
import { useApplicationData } from '../../../../../hooks/useApplicationData';
import { MdOpenInNew } from 'react-icons/md';
import { useStableParams } from '../../../../../routes/useStableParams';
import { CategoryTreeNode } from '../../../../../lib/categories/categoryTypes';
import { useClipboardHandlers } from '../../../../../lib/hooks/useClipboardHandlers';
import { computeRemSize } from '@ydistri/theme';
import SkuDescription from '../../../../../components/global/SkuDescription/SkuDescription';
import { useTemplateOrCalculationWithValue } from '../../../../../hooks/useTemplateOrCalculationWithValue';
import NoData from '../../../../../components/global/NoData/NoData';
import ProductActivityTag from '../../components/ProductActivityTag';

interface CompoundValue {
  label: string;
  value: string | string[] | React.ReactNode;
}

interface EntityRecord extends CompoundValue {
  id?: number | string;
  code?: string | null;
  systemId?: number;
  additionalData?: CompoundValue;
}

export const EntitiesTableWrapper = styled.div`
  //min-width: 32%;
  width: min(100%, ${computeRemSize(500)});
  display: flex;
  flex-grow: 1;

  & > div {
    width: 100%;
  }
`;

export const EntitiesDataTable = styled(DataTable<EntityRecord>)`
  .ant-table-tbody .ant-table-cell {
    vertical-align: top;
  }
`;

const EntitiesTable: React.FC = () => {
  const { projectShortName } = useStableParams();
  const [searchParams] = useSearchParams();
  const skuEntity = useDetailSkuEntity();
  const selectedCategoryId = useCategoryId();
  const selectedCategorySlug = useSelector(
    (state: ReduxState) => state.categoryTreeReducer.currentlySelectedCategorySlug,
  );
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: categories } = useGetCategoryTreeQuery(templateOrCalculation.value);

  const { departments: hasDepartments } = useApplicationData();

  const { storeId, productId, regionId, skuId } = useDetail(
    'storeId',
    'productId',
    'regionId',
    'skuId',
  );
  const clipboardHandlers = useClipboardHandlers();

  const skuStoreId = skuEntity?.values.WarehouseId
    ? parseInt(skuEntity.values.WarehouseId)
    : undefined;

  const { currentData: productEntity } = useGetSingleProductQuery(productId);
  const { data: storeEntity } = useGetSingleStoreQuery(skuStoreId ?? storeId);
  const { data: regionEntities } = useGetRegionsQuery(templateOrCalculation.value);
  const { currentData: sku } = useGetSkuQuery(skuId, { skip: skuId <= 0 });
  const skuClass = sku?.skuClass;
  const skuDescription = sku?.description;

  const columns = useMemo((): ColumnsType<EntityRecord> => {
    return [
      {
        dataIndex: 'label',
        width: '9rem',
        render: (text: string) => {
          return <Label>{`${text}:`}</Label>;
        },
      },
      {
        dataIndex: 'value',
        width: '45rem',
        render: (value: string | string[] | React.ReactNode, record: EntityRecord) => {
          if (Array.isArray(value)) {
            return <SimpleList values={value} />;
          } else {
            if (typeof value === 'string') {
              return (
                <Row $gap={computeRemSize(8)} $alignItems="center">
                  <Text>{value}</Text>
                  {(record.id ?? record.systemId) && (
                    <EntityId
                      id={record.id ?? null}
                      code={record.code}
                      systemId={record.systemId ?? null}
                      {...clipboardHandlers}
                    />
                  )}
                </Row>
              );
            } else {
              return value;
            }
          }
        },
      },

      {
        dataIndex: 'additionalData',
        render: (additionalData?: CompoundValue) =>
          additionalData && (
            <ReadOnlyItem label={additionalData.label} value={additionalData.value} />
          ),
      },
    ];
  }, [clipboardHandlers]);

  const data = useMemo((): EntityRecord[] => {
    const result: EntityRecord[] = [];

    if (productEntity) {
      const productLabel = 'Product';
      const productId = productEntity.id;

      //only create the link when we are looking at a SKU
      const showProductAsLink =
        searchParams.has(UrlParams.SKU) ||
        (searchParams.has(UrlParams.PRODUCT) && searchParams.has(UrlParams.STORE));

      if (showProductAsLink) {
        const path = generatePath(ROUTES.detail, {
          projectShortName: projectShortName ?? '',
          slug: selectedCategorySlug,
        });

        const newSearchParams = createSearchParams([['product', productEntity.id.toString()]]);
        const pathWithSearch = `${path}?${newSearchParams.toString()}`;

        result.push({
          id: productId,
          label: productLabel,
          value: (
            <Row $gap="4px" $alignItems="center">
              <YLink to={pathWithSearch}>
                <Row $gap="0.6rem" $alignItems="center">
                  <Text>{productEntity.name}</Text>
                  <MdOpenInNew size={IconButton_IconSizes.xSmall} />
                </Row>
              </YLink>
              <ProductActivityTag productId={productEntity.id} />
              <EntityId
                id={productEntity.customerId ?? null}
                code={productEntity.code}
                systemId={productEntity.id}
                {...clipboardHandlers}
              />
            </Row>
          ),
        });
      } else {
        result.push({
          id: productId,
          label: productLabel,
          value: (
            <Row $gap={computeRemSize(8)} $alignItems="center">
              <Text>{productEntity.name}</Text>
              <ProductActivityTag productId={productEntity.id} />
              <EntityId
                id={productEntity.customerId ?? null}
                code={productEntity.code}
                systemId={productEntity.id}
                {...clipboardHandlers}
              />
            </Row>
          ),
        });
      }

      if (hasDepartments) {
        result.push({
          label: 'Department',
          value: (
            <Row $gap={computeRemSize(8)} $alignItems="center">
              <Text>{productEntity.department?.name}</Text>
              <EntityId
                id={productEntity.department?.customerDepartmentId ?? null}
                systemId={productEntity.department?.id ?? null}
                {...clipboardHandlers}
              />
            </Row>
          ),
        });
      }
    }

    if (regionEntities && !storeEntity) {
      const region = regionEntities.object[regionId];
      if (region) {
        result.push({
          label: 'Region',
          value: region.name,
          systemId: region.id,
        });
      }
    }

    if (storeEntity) {
      result.push({
        id: storeEntity.customerWarehouseId ?? storeEntity.id,
        label: 'Store',
        value: storeEntity.name,
        code: storeEntity.code,
        systemId: storeEntity.id,
      });

      const storeType = storeEntity.warehouseType;
      if (storeType) {
        result.push({
          id: storeType.customerWarehouseTypeId ?? storeType.id,
          label: 'Store Type',
          value: storeType.name,
          systemId: storeType.id,
        });
      }

      // When a SKU is anchored, the decile must come from the same SkuAttributeValue snapshot as the
      // quintile - dbo.StorePerformance would show today's decile beside a historical quintile.
      // Otherwise (store viewed alone/store total) the decile is "now", from dbo.StorePerformance via
      // storeEntity. Either way, null covers both "never ranked" and "no matching row" - the
      // placeholder on both, never a manufactured default.
      result.push({
        label: 'Store Decile',
        value: skuEntity
          ? // eslint-disable-next-line typescript/prefer-nullish-coalescing -- '' must render as NoData too (SkuAttributeResponse.Selectors.cs collapses "no data" to '' server-side), not just null/undefined
            skuEntity.values.StoreDecile || <NoData />
          : (storeEntity.storeDecile?.toString() ?? <NoData />),
      });

      result.push({
        label: 'Region',
        value: storeEntity.region?.name,
        systemId: storeEntity.region?.id,
      });
    }

    if (productEntity) {
      result.push({
        label: 'Brand',
        value: productEntity.brand?.name,
        systemId: productEntity.brand?.id,
      });

      // Only when a SKU is anchored (store x product): a product/category viewed alone has no store
      // to anchor the quintile to. Sourced from the SkuAttributeValue pivot (skuEntity.values) -
      // never a live StoreCategoryBrandPerformance read.
      if (skuEntity) {
        result.push({
          label: 'Brand Quintile',
          // eslint-disable-next-line typescript/prefer-nullish-coalescing -- see the Store Decile push above: '' must render as NoData too, not just null/undefined
          value: skuEntity.values.BrandQuintile || <NoData />,
        });
      }
    }

    if (skuClass) {
      result.push({
        label: 'SKU Class',
        value: skuClass.name,
        systemId: skuClass.id,
      });
    }

    if (categories) {
      let categoryNodes: CategoryTreeNode[] = [];

      if (productEntity) {
        const productCategories = productEntity.allCategoryIds?.filter(id => !isRootCategory(id));
        if (productCategories) {
          categoryNodes = getCategories(productCategories, categories);
        }
      } else {
        //if the selected category is not the root category
        if (!isRootCategory(selectedCategoryId)) {
          const categoryPath = constructCategoryPath(selectedCategoryId, categories).filter(
            id => !isRootCategory(id),
          );
          categoryNodes = getCategories(categoryPath, categories);
        }
      }

      if (categoryNodes.length > 0) {
        result.push({
          label: 'Categories',
          value: <CategoriesList categories={categoryNodes} />,
        });
      }
    }

    if (skuDescription) {
      result.push({
        label: 'Description',
        value: <SkuDescription skuDescription={skuDescription} hideLabel />,
      });
    }

    return result;
  }, [
    categories,
    clipboardHandlers,
    hasDepartments,
    productEntity,
    projectShortName,
    regionEntities,
    regionId,
    searchParams,
    selectedCategoryId,
    selectedCategorySlug,
    skuClass,
    skuDescription,
    skuEntity,
    storeEntity,
  ]);

  const rowKeyProvider = useCallback((record: EntityRecord) => {
    return `${record.label}_${record.id ?? ''}`;
  }, []);

  const rowClassProvider = useCallback((record: EntityRecord) => {
    if (record.label === 'Categories') {
      return 'categories';
    }

    return '';
  }, []);

  if (data.length > 0) {
    return (
      <EntitiesTableWrapper>
        <EntitiesDataTable
          id="DetailEntityRecords"
          columns={columns}
          dataSource={data}
          rowKey={rowKeyProvider}
          rowClassName={rowClassProvider}
        />
      </EntitiesTableWrapper>
    );
  } else {
    return null;
  }
};

export default EntitiesTable;
